import CookieInformation, { CookieInformationSettings } from "./dependencies/CookieInformation";
import OneTrust, { OneTrustDependencySetting } from "./dependencies/OneTrust";
import CookieScript, { CookieScriptSettings } from "./dependencies/CookieScript";
import Cookiebot, { CookiebotSettings } from "./dependencies/Cookiebot";
import CookieTractor, { CookieTractorSettings } from "./dependencies/CookieTractor";
import React, { Component, Fragment } from "react";

import InitialConsentSettings from "./InitialConsentSettings";
import GTMEvent from "./event";
import GTMEvents from "./events";
import GTMScript from "./Script";
import DependencyBase from "./dependencies/DependecyBase";

declare global {
    interface Window {
        dataLayer: Array<any>;
        grebban: {
            gtm: GTM;
        };
    }
}

export interface GTMComponentProps {
    trackingKey: string;
    onLoad?: () => void;
    initialVariables?: object;
    helmetComponent: React.ComponentType;
    consentModeEnabled?: boolean;
    initialConsentSettings?: object;
    dependencies?: Array<
        (
            | OneTrustDependencySetting
            | CookieInformationSettings
            | CookieScriptSettings
            | CookiebotSettings
            | CookieTractorSettings
        ) & {
            name: string;
        }
    >;
    scriptProps?: {
        src?: string;
    };
}

export type Dependency = () => boolean;

export interface DefaultDependencySettings {
    consentModeEnabled?: boolean; // automatically filled from gtm
    helmetComponent: React.ComponentType;
}
interface IntegratedDependency {
    [key: string]: {
        name: string;
        component: typeof DependencyBase;
        dependency: Dependency;
        active?: boolean;
        loaded?: boolean;
        settings?:
            | CookieInformationSettings
            | OneTrustDependencySetting
            | CookieScriptSettings
            | CookiebotSettings
            | CookieTractorSettings;
    };
}

class GTM extends Component<GTMComponentProps> {
    // Keeps trying until it succeeds, or time runs out
    private static callbackAutoRetry = (callback: () => boolean, timer) => {
        setTimeout(() => {
            const callbackSuccess = callback();
            if (!callbackSuccess) {
                const callbackTimer = timer < 10000 ? timer * 2 : timer;
                GTM.callbackAutoRetry(callback, callbackTimer);
            }
        }, timer);
    };

    public static hasScriptLoaded = () => {
        return GTM.scriptLoaded;
    };

    public static scriptCallback = (name: string) => {
        if (name === "GTM") {
            if (GTM.hasScriptLoaded()) {
                throw new Error("Script has already been loaded, are you trying to load more than one script?");
            }

            this.callbackAutoRetry(() => {
                // this seems like magic...
                if (window?.["google_tag_manager"]?.[GTM.trackingKey]) {
                    GTM.scriptLoaded = true;
                    GTM.dataLayer = window["google_tag_manager"][GTM.trackingKey]["dataLayer"];
                    GTM.dataLayer.activity = window.dataLayer;
                    return true;
                }
                return false;
            }, 100);

            this.callbackAutoRetry(GTMEvents.processQueue, 100);
            return;
        } else if (GTM.integratedDependencies[name]) {
            GTM.integratedDependencies[name].loaded = true;
            GTM.integratedDependencies[name].component.onScriptLoaded();
            return;
        }
        console.warn(`GTM.scriptCallback: ${name} does not have a valid callback.`);
    };
    public static addEventDependency = (dependency: Dependency) => {
        GTMEvents.addDependency(dependency);
    };
    public static addDataLayerEvent = (name: string, data: object, callback?: () => void) => {
        const event = new GTMEvent(name, data, callback);
        GTMEvents.addEvent(event);
        return event;
    };
    public static resetDataLayer = () => {
        const event = new GTMEvent("datalayer_reset", {}, () => GTM.dataLayer.reset());
        GTMEvents.addEvent(event);
        return event;
    };
    public static getDataLayerVariable = (variable) => {
        return GTM.dataLayer?.get(variable);
    };
    public static setDataLayerVariable = (variable, value) => {
        return GTM.dataLayer?.set(variable, value);
    };

    public static debug = () => {
        return {
            trackingKey: GTM.trackingKey,
            initialVariables: GTM.initialVariables,
            events: GTMEvents?.debug(),
            scriptLoaded: GTM.hasScriptLoaded(),
            dataLayer: GTM.dataLayer,
            integratedDependencies: GTM.integratedDependencies,
            activeDependencies: Object.keys(GTM.integratedDependencies)
                .map((dependencyName) => {
                    return GTM.integratedDependencies[dependencyName];
                })
                .filter((dependency) => {
                    return dependency.active;
                }),
        };
    };

    public static defaultProps = {
        consentModeEnabled: true,
        dependencies: [],
        initialConsentSettings: {},
        initialVariables: {},
        scriptProps: {},
        onLoad: () => {},
    };

    constructor(props) {
        super(props);
        const { trackingKey, initialVariables, dependencies, helmetComponent } = this.props;

        if (!helmetComponent) {
            throw new Error("No helmetComponent prop supplied.");
        }

        if (GTM.hasScriptLoaded()) {
            throw new Error("GTM has already been initialized.");
        }

        if (!trackingKey) {
            throw new Error("No trackingKey was supplied for GTM, please supply a trackingKey.");
        }

        GTM.trackingKey = trackingKey;
        GTM.initialVariables = initialVariables || {};
        GTM.onLoad = props.onLoad;

        if (typeof window === "undefined") {
            return;
        }

        // Add initial variables to the dataLayer immediately without waiting for consent and such
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(new GTMEvent("global_variables", GTM.initialVariables));

        // Add script loader dependency to GTMEvents instance
        GTMEvents.addDependency(GTM.hasScriptLoaded);

        // And adds it to the start of the queue.

        if (dependencies && Array.isArray(dependencies)) {
            dependencies.forEach(({ name, ...settings }) => {
                const integratedDependency = GTM.integratedDependencies[name];
                if (integratedDependency) {
                    settings.helmetComponent = helmetComponent;
                    integratedDependency.active = true;
                    integratedDependency.settings = settings;

                    GTMEvents.addDependency(integratedDependency.dependency);
                } else {
                    throw new Error(
                        `Dependency ${name} could not be activated since it does not exist, have you checked the spelling or dependency compatibility? Valid dependencies are: ${Object.keys(
                            GTM.integratedDependencies,
                        ).join(",")}`,
                    );
                }
            });
        }
    }

    shouldComponentUpdate(): boolean {
        return false;
    }

    private static dataLayer: {
        get: (variable) => any;
        set: (variable, value) => any;
        reset: () => boolean;
        activity: Array<any>;
    };
    private static initialVariables = {};
    private static trackingKey: string;
    private static onLoad: () => void;
    private static scriptLoaded = false;
    private static integratedDependencies: IntegratedDependency = {
        OneTrust: {
            name: "OneTrust",
            component: OneTrust,
            dependency: OneTrust.dependency,
        },
        CookieInformation: {
            name: "CookieInformation",
            component: CookieInformation,
            dependency: CookieInformation.dependency,
        },
        CookieScript: {
            name: "CookieScript",
            component: CookieScript,
            dependency: CookieScript.dependency,
        },
        Cookiebot: {
            name: "Cookiebot",
            component: Cookiebot,
            dependency: Cookiebot.dependency,
        },
        CookieTractor: {
            name: "CookieTractor",
            component: CookieTractor,
            dependency: CookieTractor.dependency,
        },
    };

    render() {
        if (typeof window === "undefined") {
            return null;
        }

        // Add our helper to the grebban globals scope, we do this for easy access for 3:rd party scripts and debugging.
        window.grebban = window.grebban || {};
        window.grebban.gtm = window.grebban.gtm || GTM;

        return (
            <Fragment>
                {Object.values(GTM.integratedDependencies).map(({ component, active, name, settings }) => {
                    const DependencyComponent = component;
                    return active ? <DependencyComponent key={name} {...settings} /> : null;
                })}

                {this.props.dependencies?.some((dependency) => dependency.consentModeEnabled) && (
                    <InitialConsentSettings
                        helmetComponent={this.props.helmetComponent}
                        {...this.props.initialConsentSettings}
                    />
                )}

                <GTMScript helmetComponent={this.props.helmetComponent} scriptSrc={this.props.scriptProps?.src} />
            </Fragment>
        );
    }
}

export default GTM;
